import React, { useEffect, useRef, useState } from 'react';
import notificationService from 'services/notificationService';
import { money, parsePrice } from './priceBoardMath';

/** Background of a cell with an unsaved change (Price Board). */
export const changedBg = 'rgba(245, 158, 11, 0.18)';

// ---------------------------------------------------------------- one editable cell

interface CellProps {
  value: number | null;
  /** Shown grey with · when the cell follows the menu price. */
  followsPrice?: number;
  isMenu: boolean;
  changed: boolean;
  editable: boolean;
  rowIndex: number;
  colIndex: number;
  label: string;
  onCommit: (value: number | null) => void;
}

const PriceCell: React.FC<CellProps> = ({ value, followsPrice, isMenu, changed, editable, rowIndex, colIndex, label, onCommit }) => {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  /** Editing shows the plain number; otherwise it shows money like the grey cells, so both read as prices. */
  const [focused, setFocused] = useState(false);
  const cancelled = useRef(false);
  useEffect(() => { setDraft(value == null ? '' : String(value)); }, [value]);
  /** An outlet's own price can be put back to following the menu in one click. */
  const canFollow = editable && !isMenu && value != null;

  const style: React.CSSProperties = {
    width: '100%', textAlign: 'right', padding: canFollow ? '2px 6px 2px 24px' : '2px 6px', fontSize: 13, height: 28,
    background: changed ? changedBg : 'transparent', fontWeight: changed ? 600 : undefined,
    border: '1px solid var(--border-color)', borderRadius: 4, color: 'var(--text-primary)',
  };

  if (!editable) {
    return (
      <div style={{ ...style, border: 'none', lineHeight: '24px', color: value == null ? 'var(--text-muted)' : undefined }}>
        {value == null ? `${money(followsPrice ?? 0)} ·` : money(value)}
      </div>
    );
  }

  const commit = () => {
    setFocused(false);
    if (cancelled.current) { cancelled.current = false; return; }
    const parsed = parsePrice(draft);
    if (!parsed.ok) {
      notificationService.toast.warning(`${label}: enter a price such as 120 or 99.50.`);
      setDraft(value == null ? '' : String(value));
      return;
    }
    if (isMenu && parsed.value === null) {
      notificationService.toast.warning(`${label}: the menu price cannot be empty.`);
      setDraft(value == null ? '' : String(value));
      return;
    }
    // A following cell left at the menu price it was opened with keeps following the menu.
    if (!isMenu && value === null && parsed.value === followsPrice) { setDraft(''); return; }
    if (parsed.value !== value) onCommit(parsed.value);
  };

  return (
    <div style={{ position: 'relative' }}>
    {canFollow && (
      <button
        type="button"
        aria-label={`${label}: follow the menu price`}
        title={`Follow the menu price (${money(followsPrice ?? 0)}) again instead of this outlet's own price`}
        // mousedown, not click: the input must not commit its draft first.
        onMouseDown={(e) => { e.preventDefault(); onCommit(null); }}
        style={{ position: 'absolute', left: 2, top: 4, width: 20, height: 20, padding: 0, border: 'none',
          background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, lineHeight: '20px' }}
      >↺</button>
    )}
    <input
      aria-label={label}
      inputMode="decimal"
      data-pb-row={rowIndex}
      data-pb-col={colIndex}
      value={focused ? draft : (value == null ? '' : money(value))}
      placeholder={followsPrice != null ? `${money(followsPrice)} ·` : ''}
      title={!isMenu && value == null
        ? 'Follows the menu price. Change it to give this outlet its own price.'
        : !isMenu ? "This outlet's own price. ↺ makes it follow the menu price again." : undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          // The blur below runs before the reset reaches state, so the commit is told to skip.
          cancelled.current = true;
          setDraft(value == null ? '' : String(value));
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const step = e.key === 'ArrowUp' ? -1 : 1;
          const next = document.querySelector<HTMLInputElement>(`input[data-pb-col="${colIndex}"][data-pb-row="${rowIndex + step}"]`);
          if (next) next.focus(); else (e.target as HTMLInputElement).blur();
        }
      }}
      onFocus={(e) => {
        // A cell that follows the menu opens with the menu price in it, so it can be edited like any other.
        const input = e.target;
        setFocused(true);
        if (!isMenu && value === null && followsPrice != null) setDraft(String(followsPrice));
        else setDraft(value == null ? '' : String(value));
        requestAnimationFrame(() => input.select());
      }}
      style={style}
    />
    </div>
  );
};

export default PriceCell;
